import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/supabase';
import { InterviewQuestion } from '../types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Generate interview link
export function generateInterviewLink(token: string): string {
  return `${appUrl}/interview/${token}`;
}

// Get interview by token (public access)
export async function getInterviewByToken(token: string) {
  try {
    const { data: interviews, error } = await supabase
      .from('interviews')
      .select(`
        *,
        interview_responses (
          id,
          candidate_name,
          candidate_email,
          status,
          responses,
          video_urls,
          score,
          feedback
        )
      `)
      .eq('share_token', token)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      console.error('Error fetching interview:', error);
      throw error;
    }

    if (!interviews || interviews.length === 0) {
      throw new Error('Interview not found');
    }

    const interview = interviews[0];

    // Verify interview has questions configured
    if (!interview.config?.questions?.length) {
      throw new Error('Interview questions not configured');
    }

    return interview;
  } catch (error) {
    console.error('Error getting interview by token:', error);
    throw error;
  }
}

// Create interview response (public access)
export async function createInterviewResponse(
  interviewId: string,
  candidateName: string,
  candidateEmail: string
) {
  try {
    // Verify the interview exists and is active
    const { data: interviews, error: interviewError } = await supabase
      .from('interviews')
      .select('id, status, config')
      .eq('id', interviewId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1);

    if (interviewError || !interviews || interviews.length === 0) {
      throw new Error('Interview not found or not active');
    }

    const interview = interviews[0];

    // Verify questions are configured
    if (!interview.config?.questions?.length) {
      throw new Error('Interview questions not configured');
    }

    // Create the interview response
    const { data: responses, error } = await supabase
      .from('interview_responses')
      .insert({
        interview_id: interviewId,
        candidate_name: candidateName,
        candidate_email: candidateEmail,
        status: 'pending',
        responses: [],
        video_urls: [],
        score: 0,
        feedback: {}
      })
      .select()
      .order('created_at', { ascending: false })
      .limit(1);

    if (error || !responses || responses.length === 0) {
      console.error('Error creating interview response:', error);
      throw error || new Error('Failed to create interview response');
    }

    // Update interview status to in-progress
    await supabase
      .from('interviews')
      .update({ status: 'in-progress' })
      .eq('id', interviewId);

    return responses[0];
  } catch (error) {
    console.error('Error in createInterviewResponse:', error);
    throw error;
  }
}

// Create a new interview
export async function createInterview(jobTitle: string, config: any) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error('Authentication required');
    }

    const { data: interviews, error } = await supabase
      .from('interviews')
      .insert({
        job_title: jobTitle,
        config,
        created_by: session.user.id,
        share_token: crypto.randomUUID(),
        status: 'draft'
      })
      .select()
      .order('created_at', { ascending: false })
      .limit(1);

    if (error || !interviews || interviews.length === 0) {
      console.error('Error creating interview:', error);
      throw error || new Error('Failed to create interview');
    }

    return interviews[0];
  } catch (error) {
    console.error('Error in createInterview:', error);
    throw error;
  }
}

// Update interview questions
export async function updateInterviewQuestions(
  interviewId: string,
  questions: InterviewQuestion[],
  status?: string
) {
  try {
    const { data: interviews, error: getError } = await supabase
      .from('interviews')
      .select('config')
      .eq('id', interviewId)
      .order('created_at', { ascending: false })
      .limit(1);

    if (getError || !interviews || interviews.length === 0) {
      throw getError || new Error('Interview not found');
    }

    const interview = interviews[0];

    const updatedConfig = {
      ...interview.config,
      questions: questions.map((q, index) => ({
        ...q,
        order: index + 1
      }))
    };

    const { data: updatedInterviews, error } = await supabase
      .from('interviews')
      .update({
        config: updatedConfig,
        status: status || 'active'
      })
      .eq('id', interviewId)
      .select()
      .order('created_at', { ascending: false })
      .limit(1);

    if (error || !updatedInterviews || updatedInterviews.length === 0) {
      throw error || new Error('Failed to update interview questions');
    }

    return updatedInterviews[0];
  } catch (error) {
    console.error('Error updating interview questions:', error);
    throw error;
  }
}

// Get all interviews for authenticated user
export async function getInterviews() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error('Authentication required');
    }

    const { data, error } = await supabase
      .from('interviews')
      .select(`
        *,
        interview_responses (
          id,
          candidate_name,
          candidate_email,
          status,
          score,
          completed_at
        )
      `)
      .eq('created_by', session.user.id)
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error getting interviews:', error);
    throw error;
  }
}

// Upload video recording
export async function uploadVideo(
  interviewId: string,
  responseId: string,
  questionNumber: number,
  videoBlob: Blob
): Promise<string> {
  try {
    const fileName = `${interviewId}/${responseId}/${questionNumber}.webm`;
    
    const { error: uploadError } = await supabase.storage
      .from('interview-videos')
      .upload(fileName, videoBlob, {
        contentType: 'video/webm',
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      console.error('Error uploading video:', uploadError);
      throw uploadError;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('interview-videos')
      .getPublicUrl(fileName);

    return publicUrl;
  } catch (error) {
    console.error('Error uploading video:', error);
    throw error;
  }
}

// Get interview results with feedback and score
export async function getInterviewResults(interviewId: string) {
  try {
    const { data, error } = await supabase
      .from('interview_responses')
      .select(`
        id,
        candidate_name,
        candidate_email,
        score,
        feedback,
        responses,
        video_urls,
        completed_at
      `)
      .eq('interview_id', interviewId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching interview results:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error getting interview results:', error);
    throw error;
  }
}